/**
 * Tabbed details panel showing patient info, keywords, and SF data during a SIM session.
 *
 * @module components/sim/takeSim/TakeSimDetails
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, { useEffect, useState } from 'react';
import FormHelperText from '@material-ui/core/FormHelperText';
import Alert from '@material-ui/lab/Alert';
import {
    Grid,
    Card,
    CardContent,
    Avatar,
    Tab,
    Box,
    Button,
    Toolbar,
    Paper,
    Icon,
    Badge,
    Tooltip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    NativeSelect,
} from '@material-ui/core';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';

import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
// import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import GetAppIcon from '@material-ui/icons/GetApp';
import LinkIcon from '@material-ui/icons/Link';
import { useLocation } from 'react-router-dom';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import Clone from '../../../assets/images/icons/Clone.png';
import Preview from '../../../assets/images/icons/Preview.png';
import Launch from '../../../assets/images/icons/Launch.png';
import Note from '../../../assets/images/icons/Note.png';
import Distance from '../../../assets/images/icons/Distance.png';
import Suitcase from '../../../assets/images/icons/Suitcase_redbg.png';
import { fetchDemographics, saveCaseVaules } from '../../../redux/sims/actions';
import { fetchCaseCaller } from '../../../redux/sf/actions';
import { saveTakesimInput } from '../../../services/result.service';
// import { before } from "lodash";
import KeywordInput from './KeywordInput';
import {
    SmallAvatar,
    useTabContentStyles,
    useNavbarStyles,
    useBasicInfoStyles,
    AntTabs,
} from './TakeSimDetails.js';

/**
 * AntTab is a customized variant of the Material-UI Tab component.
 * It applies specific styles to modify the appearance and behavior of tabs,
 * such as text formatting, spacing, and selection state styling.
 *
 * The customization is achieved using the withStyles Higher Order Component (HOC),
 * which incorporates theme-based values for consistent design.
 *
 * Properties used in customization:
 * - `textTransform`: Disables text transformation.
 * - `minWidth` and `minHeight`: Sets the minimum width and height.
 * - `fontSize` and `fontWeight`: Controls text size and weight.
 * - `marginRight`: Adds spacing between tabs.
 * - The `&$selected` pseudo-class: Adjusts styles specifically for the currently selected tab.
 * - The `&:focus` pseudo-class: Adjusts the color for the focused tab.
 *
 * The customized AntTab ensures a consistent and visually distinct
 * style for tab components within a theme-based application.
 */
const AntTab = withStyles((theme) => ({
    root: {
        textTransform: 'none',
        minWidth: 72,
        minHeight: '2px',
        fontSize: 12,
        fontWeight: theme.typography.fontWeightRegular,
        marginRight: theme.spacing(1),
        '&$selected': {
            color: 'rgb(38,65,114)',
            fontWeight: 600,
        },
        '&:focus': {
            color: 'rgb(38,65,114)',
        },
    },
    selected: {},
}))((props) => <Tab disableRipple {...props} />);

/**
 * TabPanel component used for rendering the content of a specific tab based on its index.
 *
 * The component conditionally renders its children based on whether the current `value`
 * matches the provided `index`. This ensures that only the content of the active tab
 * is displayed, while the others are hidden.
 *
 * @param {Object} props - The props object for the TabPanel component.
 * @param {React.ReactNode} props.children - The content to be displayed within the TabPanel when active.
 * @param {number} props.value - The currently selected tab's index.
 * @param {number} props.index - The index associated with this specific TabPanel.
 * @param {Object} [props.other] - Additional properties to be spread onto the root element.
 */
const TabPanel = (props) => {
    const {
        children, value, index, ...other
    } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={3}>
                    <div>{children}</div>
                </Box>
            )}
        </div>
    );
};

/**
 * A functional component responsible for managing and displaying demographic-related data.
 *
 * This component handles the fetching, organization, and manipulation of demographic data
 * based on given keywords, user interactions, and context. It includes features for auto-populating
 * fields, resetting data on level restarts, and processing user input.
 *
 * @param {Object} props - The props provided to the component.
 * @param {Array} props.keywords - The list of keyword objects used for structuring and displaying demographic data.
 * @param {Function} props.fetchCaseCaller - Callback for retrieving details about a case caller.
 * @param {Function} props.fetchDemographics - Callback for fetching demographic data based on the simulation ID.
 * @param {Object} props.currentClip - Contains data about the currently active clip, with an auto-populate timer and other configurations.
 * @param {boolean} props.isRestartLevel - Flag indicating whether the current level should be restarted, triggering data reset.
 * @param {string} props.type - The type of the tab or demographic section being rendered.
 * @param {number|string} props.value - The current selected index or value used to retrieve a specific keyword's data.
 * @param {boolean} props.cancel - A flag indicating whether certain operations should be canceled.
 * @param {Function} props.setCancel - Function to update the cancel state.
 *
 * @returns {JSX.Element} A grid layout displaying the demographic fields and data.
 */
const Demographics = ({
    keywords,
    fetchCaseCaller,
    fetchDemographics,
    currentClip,
    isRestartLevel,
    type,
    value,
    cancel,
    setCancel,
}) => {
    const classes = useTabContentStyles();
    const [callerName, setCallerName] = useState('');
    // const [textCount, setTextCount] = useState(0);
    // const [otherCount, setOtherCount] = useState(0);
    // const [caseDetails, setCaseDetails] = useState({});
    const [autoPopulateDataArray, setAutoPopulateDataArray] = useState([]);
    const [data, setData] = React.useState([]);

    useEffect(() => {
        if (keywords.length && keywords[parseInt(value, 10)]) {
            setData(keywords[parseInt(value, 10)].sections);
        }
    }, [value, keywords]);

    useEffect(() => {
        const simId = localStorage.getItem('sim_id');
        fetchDemographics(simId);
    }, [fetchDemographics]);

    useEffect(() => {
        setTimeout(
            () => setAutopopulate(keywords, currentClip),
            currentClip.autopopulate_timer * 1000,
        );
        setAutoPopulateDataArray(keywords);
    }, [currentClip, keywords]);

    useEffect(() => {
        if (isRestartLevel) {
            const autoPopulateArray = [...autoPopulateDataArray];
            autoPopulateArray.map((keyword) => {
                if (keyword.fields?.length) {
                    keyword.fields.map((field) => {
                        delete field.current_value;
                        return field;
                    });
                }
                return keyword;
            });
            setAutoPopulateDataArray(autoPopulateArray);
        }
    }, [isRestartLevel]);

    /**
     * Updates the autopopulate data used for dynamically populating fields within the application.
     *
     * This function manages the autopopulate logic by updating current field values based on the enabled state of the fields
     * within the `autopopulate_data` property of the `currentClip` parameter.
     *
     * If the `autoPopulateDataArray` already contains elements, they are used as the base array.
     * Otherwise, the `keyword` array is used as the base array for the autopopulate data.
     *
     * Each field within the `autopopulate_data` is iterated over, and if a field is marked as enabled,
     * its `current_value` property is updated with the corresponding `value` present in the base autopopulate
     * array (`autoPopulateArray`).
     *
     * After processing, the updated `autoPopulateArray` is stored into the state by calling the `setAutoPopulateDataArray` function.
     *
     * @param {Array} keyword - An array representing the base keywords used for autopopulation when no other data exists.
     * @param {Object} currentClip - The object containing `autopopulate_data` that specifies fields and their enabled states,
     *                                which are used to dynamically populate field values.
     */
    const setAutopopulate = (keyword, currentClip) => {
        const autoPopulateArray = autoPopulateDataArray.length
            ? [...autoPopulateDataArray]
            : [...keyword];
        const { autopopulate_data } = currentClip;
        if (autopopulate_data && autoPopulateArray.length) {
            autopopulate_data.map((value, i) => {
                value.fields.map((val, j) => {
                    if (val.is_enabled) {
                        autoPopulateArray[i].fields[j].current_value = autoPopulateArray[i].fields[j].value;
                    }
                    return val;
                });
                return value;
            });
            setAutoPopulateDataArray(autoPopulateArray);
        }
    };

    /**
     * Handles the event for fetching caller details based on user input.
     * If the 'Enter' key is pressed, it triggers the `fetchCaseCaller` function
     * with the current caller name. Otherwise, it updates the caller name
     * with the value from the event target.
     *
     * @param {Object} e - The event object triggered by user interaction.
     * @param {string} e.key - The key pressed during the event.
     * @param {Object} e.target - The target element of the event.
     * @param {string} e.target.value - The current value of the target element.
     */
    const fetchCaseCallerDetails = (e) => {
        if (e.key === 'Enter') {
            fetchCaseCaller(callerName);
        } else {
            setCallerName(e.target.value);
        }
    };

    /**
     * Handles the blur event for an input element.
     *
     * This function performs specific actions when an input element loses focus
     * (onBlur event) under certain conditions. It checks if the element is
     * scorable and has a current value, then manages the associated data
     * accordingly. While portions of the logic are commented out, the intention
     * appears to handle different input types such as text and checkboxes, and
     * update an array (`caseArray`) based on the element's data attributes and state.
     *
     * @param {Object} e - The event object associated with the blur event.
     * @param {Object} e.target - The DOM element that triggered the event.
     * @param {string} e.target.dataset.isscorable - A dataset attribute indicating
     * if the input is scorable (expected values: "true", "false").
     * @param {string} e.target.dataset.currentvalue - The current value of the input
     * as specified in its dataset.
     * @param {string} e.target.type - The type of the input element (e.g., "text", "checkbox").
     * @param {string} [e.target.dataset.inputtype] - The type of input specified in the dataset (optional, applicable for certain cases).
     * @param {string} [e.target.dataset.caseid] - The unique identifier for a case specified in the dataset (optional, applicable for certain cases).
     */
    const handleBlur = (e) => {
        if (
            e.target.dataset.isscorable === 'true'
            && e.target.dataset.currentvalue
        ) { } // eslint-disable-line no-empty
    };

    /**
     * Updates the current value and tab type of a specific field in a nested data structure
     * based on the provided event, parent index, and field index. Additionally, updates
     * the state of the auto-populate data array and resets a cancellation flag if applicable.
     *
     * @param {Object} e - The event object containing the new value for the field.
     * @param {number} parentIndex - The index of the parent object within the data array.
     * @param {number} fieldIndex - The index of the field within the parent's fields array.
     */
    const handleChange = (e, parentIndex, fieldIndex) => {
        const autopopulateArray = [...data];
        autopopulateArray[parentIndex].fields[fieldIndex].current_value = e.target.value;
        autopopulateArray[parentIndex].fields[fieldIndex].tabType = type;
        setAutoPopulateDataArray(autopopulateArray);
        if (cancel) setCancel(false);
    };

    return (
        <Grid container spacing={3}>
            {data.map((keyword, index) => (
                <Grid item xs={12} key={keyword._id} className={classes.gridTab}>
                    <Typography className={classes.title}>
                        <ArrowDropDownIcon className={classes.arrowDropDown} />
                        {keyword.section_name}
                    </Typography>
                    <Grid container spacing={3} className={classes.grid}>
                        {keyword.fields.map((fieldName, i) => (
                            <Grid
                                item
                                xs={6}
                                key={fieldName.field_title}
                                className={classes.gridItem}
                            >
                                <FormHelperText>{fieldName.field_title}</FormHelperText>
                                {(fieldName.type).toLowerCase() === 'text' ? (
                                    <KeywordInput
                                        handleChange={(e) => handleChange(e, index, i)}
                                        classes={classes}
                                        handleBlur={handleBlur}
                                        fieldName={fieldName}
                                        fetchCaseCallerDetails={fetchCaseCallerDetails}
                                    />
                                ) : (
                                    <NativeSelect
                                        value={fieldName.current_value}
                                        onChange={(e) => handleChange(e, index, i)}
                                        className={classes.select}
                                        fullWidth
                                        required
                                    >
                                        {fieldName && fieldName.options && fieldName.options.map((option) => (
                                            <option value={option} key={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </NativeSelect>
                                )}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            ))
            }
        </Grid>
    );
};

/**
 * The `History` component renders a list of history entries and displays details such as date, field, user,
 * original value, and new value for each entry. It includes a button to view all history items.
 *
 * @param {Object} props - The properties for the History component.
 * @param {Array} props.history - An array of history objects containing details for each case history.
 * @param {string} props.history[].Id - The unique identifier of the history entry.
 * @param {string} props.history[].CreatedDate - The date when the history entry was created.
 * @param {string} props.history[].Field - The field associated with the history entry.
 * @param {string} props.history[].CreatedById - The identifier of the user who created the history entry.
 * @param {string} props.history[].OldValue - The original value before the change.
 * @param {string} props.history[].NewValue - The new value after the change.
 */
const History = ({ history }) => {
    const classes = useBasicInfoStyles();

    return (
        <div>
            {history.map((caseHistory, index) => (
                <Grid
                    container
                    key={caseHistory.Id}
                    className={index === 3 ? '' : classes.border}
                >
                    <Grid item xs={6} className={classes.history}>
                        <span> Date:</span>
                    </Grid>
                    <Grid item xs={6} className={classes.txtAlign}>
                        <span>{moment(caseHistory.CreatedDate).format('MM-DD-YYYY')}</span>
                    </Grid>
                    <Grid item xs={6} className={classes.history}>
                        <span>Field:</span>
                    </Grid>
                    <Grid item xs={6} className={classes.txtAlign}>
                        <span>{caseHistory.Field}</span>
                    </Grid>
                    <Grid item xs={6} className={classes.history}>
                        <span>User:</span>
                    </Grid>
                    <Grid item xs={6} className={classes.txtAlign}>
                        <span>{caseHistory.CreatedById}</span>
                    </Grid>
                    <Grid item xs={6} className={classes.history}>
                        <span>Orginal Value:</span>
                    </Grid>
                    <Grid item xs={6} className={classes.txtAlign}>
                        <span>{caseHistory.OldValue}</span>
                    </Grid>
                    <Grid item xs={6} className={classes.history}>
                        <span>New Value:</span>
                    </Grid>
                    <Grid item xs={6} className={classes.txtAlign}>
                        <span>{caseHistory.NewValue}</span>
                    </Grid>
                </Grid>
            ))}
            <div className={classes.center}>
                <Button variant="contained" className={classes.button} disableElevation>
                    VIEW ALL
                </Button>
            </div>
        </div>
    );
};

/**
 * Navbar component that renders a tabbed navigation interface along with a demographics display.
 *
 * @param {Object[]} keywords - Array of keyword objects, each representing a tab. Each object should contain `_id` and `tab_name` properties.
 * @param {Object[]} layoutData - Array of layout configuration objects used to determine the type of the current layout.
 * @param {number} layoutIndex - Index of the current layout in the `layoutData` array.
 * @param {Function} fetchCaseCaller - Function to fetch case caller data.
 * @param {Function} fetchDemographics - Function to fetch demographic data.
 * @param {Object} currentClip - Object representing the currently selected clip.
 * @param {boolean} isRestartLevel - Flag indicating whether to restart a level in the process.
 * @param {boolean} cancel - State indicating if a certain action is being canceled.
 * @param {Function} setCancel - Function to set the `cancel` state.
 * @returns {React.ReactNode} Renders a navigation bar with tabs and a demographics section.
 */
const Navbar = ({
    keywords,
    layoutData,
    layoutIndex,
    fetchCaseCaller,
    fetchDemographics,
    currentClip,
    isRestartLevel,
    cancel,
    setCancel,
}) => {
    const classes = useNavbarStyles();
    const [value, setValue] = React.useState(0);
    const handleTabs = (e, newValue) => {
        setValue(newValue);
    };

    useEffect(() => {
        setValue(0);
    }, [layoutIndex]);

    return (
        <div className={classes.navbar}>
            <AntTabs value={value} onChange={handleTabs} className={classes.antTabs}>
                {
                    keywords.map((tab) => (
                        <AntTab
                            key={tab._id}
                            label={tab.tab_name}
                            className={classes.tab}
                        />
                    ))
                }
            </AntTabs>
            <Demographics
                layoutIndex={layoutIndex}
                keywords={keywords}
                value={value}
                cancel={cancel}
                fetchCaseCaller={fetchCaseCaller}
                fetchDemographics={fetchDemographics}
                currentClip={currentClip}
                isRestartLevel={isRestartLevel}
                type={layoutData.length ? layoutData[layoutIndex].type : ''}
                setCancel={setCancel}
            />
        </div>
    );
};

/**
 * Represents the details and behavior of the simulation interface in the application.
 *
 * @param {Object} takeSim - Information about the current simulation, including `name` and `profile_pic`.
 * @param {Array<Object>} keywords - List of simulation data associated with tabs, types, and sections.
 * @param {Function} fetchDemographics - Function to fetch demographic data for the current simulation.
 * @param {Function} fetchCaseCaller - Function to fetch specific case-based data.
 * @param {Object} currentClip - Current clip data associated with the simulation.
 * @param {boolean} isRestartLevel - Indicates whether the simulation level is restarting.
 * @param {Object} userResponse - User's response object tied to the simulation.
 *
 * @returns {JSX.Element} Component rendering the simulation interface, including user interactions to
 *          manage and manipulate simulation tabs, sections, and associated input data.
 */
const TakeSimDetails = ({
    takeSim,
    keywords,
    fetchDemographics,
    fetchCaseCaller,
    currentClip,
    isRestartLevel,
    userResponse,
}) => {
    const location = useLocation();
    const classes = useBasicInfoStyles();
    const [openDocuments, setOpenDocuments] = React.useState(false);
    const [openLinks, setOpenLinks] = React.useState(false);
    const [tabArray, setTabArray] = React.useState([]);
    const [type, setType] = React.useState('case');
    const [toggleIndex, setToggleIndex] = useState(0);
    const [openAlert, setOpenAlert] = React.useState(false);
    const [cancel, setCancel] = useState(false);

    localStorage.setItem('sims_id', localStorage.getItem('sim_id'));

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (keywords.length) {
            setTabArray(keywords[0].tabs ? keywords[0].tabs : []);
            setType(keywords[0].type);
        } else {
            const simId = localStorage.getItem('sim_id');
            fetchDemographics(simId);
        }
    }, [keywords, fetchDemographics]);

    useEffect(() => {
        setInterval(() => {
            setOpenAlert(false);
        }, 6000);
    }, [openAlert]);

    /**
     * Callback function to handle the closing of an alert.
     *
     * @param {Object} event - The event object associated with the close action.
     * @param {string} reason - The reason for the closure of the alert. If the reason
     *                          is 'clickaway', the closure action will be ignored.
     */
    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenAlert(false);
    };

    /**
     * Updates the current type and performs related state updates.
     *
     * @function typeChange
     * @param {string} type - The new type to be set.
     * @description This function updates the current type and performs various state changes
     * including resetting the cancel flag, filtering the keywords array to find matching
     * entries, updating the tab array, and setting the toggle index based on the matching type.
     */
    const typeChange = (type) => {
        setType(type);
        setCancel(false);
        // eslint-disable-next-line array-callback-return
        keywords.filter((keyword, index) => {
            if (keyword.type === type) {
                setTabArray(keyword.tabs);
                setToggleIndex(index);
            }
        });
    };

    /**
     * An asynchronous function to handle the save operation. This function processes keywords
     * to extract sections from their tabs, then saves the processed data using the `saveTakesimInput` function.
     *
     * The function retrieves the simulation ID from the local storage and utilizes the user response
     * ID along with the processed sections for the save operation.
     *
     * @function handleSave
     * @async
     * @returns {Promise<void>} Resolves once the save operation is successfully completed.
     */
    const handleSave = async () => {
        const sections = [];
        keywords.map((keyword) => {
            keyword.tabs.map((tab) => {
                sections.push(...tab.sections);
                return true;
            });
            return true;
        });
        await saveTakesimInput(localStorage.getItem('sim_id'), userResponse._id, sections);
        //setOpenAlert(true);
    };

    /**
     * Handles the cancellation action by resetting the current values of fields within sections of tabs.
     *
     * This function processes an array of tabs, iterating through each tab's sections and their respective fields.
     * During this process, it clears the `current_value` property of each field and invokes a state-setting operation to reflect the cancellation state.
     */
    const handleCancel = async () => {
        // setTabArray([]);
        const data = tabArray;
        data.map((tab) => {
            tab.sections.map((section) => {
                section.fields.map((field) => {
                    field.current_value = '';
                    setCancel(true);
                    return true;
                });
                return true;
            });
            return true;
        });
        setTabArray(data);
    };

    return (
        <Paper elevation={0}>
            {openAlert ? (
                <Alert
                    variant="filled"
                    onClose={handleAlertClose}
                    severity="success"
                >
                    Inputs saved successfully
                </Alert>
            ) : null}
            <Card>
                <CardContent className={classes.background}>
                    <Toolbar className={classes.background}>
                        <Grid className={classes.title} container wrap="nowrap" spacing={2}>
                            <Grid item>
                                <Badge
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    badgeContent={
                                        <SmallAvatar variant="rounded" src={Suitcase} />
                                    }
                                >
                                    <Avatar
                                        variant="rounded"
                                        alt="Travis Howard"
                                        src={takeSim.profile_pic}
                                    />
                                </Badge>
                            </Grid>
                            <Grid item xs={9} zeroMinWidth>
                                <Typography className={classes.title} noWrap>
                                    <span className={classes.case}>CASE</span>
                                    <br />
                                    {takeSim.name}
                                </Typography>
                            </Grid>
                        </Grid>
                        {location.state.documents && location.state.documents.length ? (
                            <Tooltip
                                title="Download documents"
                                placement="top"
                            >
                                <IconButton className={classes.icon} onClick={() => setOpenDocuments(true)}>
                                    <Icon>
                                        <GetAppIcon className={classes.download} />
                                    </Icon>
                                </IconButton>
                            </Tooltip>
                        ) : ''}
                        {location.state.hotLinks && location.state.hotLinks.length ? (
                            <Tooltip
                                title="Hot Link URL(s)"
                                placement="top"
                            >
                                <IconButton className={classes.icon} onClick={() => setOpenLinks(true)}>
                                    <Icon>
                                        <LinkIcon className={classes.download} />
                                    </Icon>
                                </IconButton>
                            </Tooltip>
                        ) : ''}
                        <ToggleButtonGroup
                            value={type}
                            onChange={(event, newAlignment) => {
                                setType(newAlignment);
                            }}
                            exclusive
                            aria-label="text alignment"
                            className={classes.toggleBtn}
                        >
                            <ToggleButton
                                value="case"
                                aria-label="left aligned"
                                onClick={() => { typeChange('case'); }}
                            >
                                Case
                            </ToggleButton>
                            <ToggleButton
                                value="account"
                                aria-label="right aligned"
                                onClick={() => { typeChange('account'); }}
                            >
                                Account
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <IconButton className={classes.icon}>
                            <Icon>
                                <img
                                    className={classes.img}
                                    src={Launch}
                                    height={20}
                                    width={20}
                                    alt="print"
                                />
                            </Icon>
                        </IconButton>
                        <IconButton className={classes.icon}>
                            <Icon>
                                <img
                                    className={classes.img}
                                    src={Clone}
                                    height={20}
                                    width={20}
                                    alt="print"
                                />
                            </Icon>
                        </IconButton>
                        <IconButton className={classes.icon}>
                            <Icon>
                                <img
                                    className={classes.img}
                                    src={Distance}
                                    height={20}
                                    width={20}
                                    alt="print"
                                />
                            </Icon>
                        </IconButton>
                        <IconButton className={classes.icon}>
                            <Icon>
                                <img
                                    className={classes.img}
                                    src={Note}
                                    height={20}
                                    width={20}
                                    alt="print"
                                />
                            </Icon>
                        </IconButton>
                        <IconButton className={classes.icon}>
                            <Icon>
                                <img
                                    className={classes.img}
                                    src={Preview}
                                    height={20}
                                    width={20}
                                    alt="print"
                                />
                            </Icon>
                        </IconButton>
                    </Toolbar>
                    <Dialog
                        open={openDocuments}
                        onClose={() => { setOpenDocuments(false); }}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">Download Documents</DialogTitle>
                        <DialogContent>
                            <List>
                                {location.state.documents && location.state.documents.map((doc, index) => (
                                    <ListItem key={doc._id}>
                                        <ListItemText>
                                            {index + 1}
                                            .
                                            {' '}
                                            {doc.name}
                                        </ListItemText>
                                        <ListItemSecondaryAction className={classes.listAction}>
                                            <IconButton href={doc.document_url} target="blank">
                                                {' '}
                                                {' '}
                                                <GetAppIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setOpenDocuments(false)}
                                className={classes.cancelButton}
                            >
                                Cancel
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <Dialog
                        open={openLinks}
                        onClose={() => { setOpenLinks(false); }}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">Hot Link URL(s)</DialogTitle>
                        <DialogContent>
                            <List>
                                {location.state.hotLinks && location.state.hotLinks.map((link, index) => (
                                    <ListItem key={index} component="a" href={link} target="blank">
                                        <ListItemText title={link.length > 55 ? link : null}>
                                            {index + 1}
                                            .
                                            {' '}
                                            {link.length > 55
                                                ? `${link.slice(0, 55)}  ...`
                                                : link}
                                        </ListItemText>
                                    </ListItem>
                                ))}
                            </List>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setOpenLinks(false)}
                                className={classes.cancelButton}
                            >
                                Cancel
                            </Button>
                        </DialogActions>
                    </Dialog>
                </CardContent>

                <CardContent className={classes.bgContent}>
                    <Grid container spacing={0}>
                        <Grid item xs={2}>
                            <span className={classes.topHeaderFont}>Case Number</span>
                            <br />
                            <span className={classes.bottomHeaderFont}>
                                {takeSim.caseNumber}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className={classes.topHeaderFont}>Case Record Type</span>
                            <br />
                            <span className={classes.bottomHeaderFont}>
                                {takeSim.caseRecordType}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className={classes.topHeaderFont}>Date/Time opened</span>
                            <br />
                            <span className={classes.bottomHeaderFont}>
                                {takeSim.openedDate}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className={classes.topHeaderFont}>
                                Hours/Days on the case
                            </span>
                            <br />
                            <span className={classes.bottomHeaderFont}>
                                {takeSim.passedTime}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className={classes.topHeaderFont}>Recent Case</span>
                            <br />
                            <span className={classes.bottomHeaderFont}>
                                {takeSim.recentCase}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className={classes.topHeaderFont}>Recent Case Date</span>
                            <br />
                            <span className={classes.bottomHeaderFont}>
                                {takeSim.recentCaseDate}
                            </span>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card className={classes.keywordSelectionGrid}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Navbar
                            keywords={tabArray}
                            layoutData={keywords}
                            fetchCaseCaller={fetchCaseCaller}
                            fetchDemographics={fetchDemographics}
                            currentClip={currentClip}
                            isRestartLevel={isRestartLevel}
                            layoutIndex={toggleIndex}
                            cancel={cancel}
                            setCancel={setCancel}
                        />
                    </Grid>
                    {
                        keywords.length
                            ? (
                                <div className={classes.footerButton}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        disableElevation
                                        className={classes.fBtn}
                                        onClick={handleSave}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        className={classes.fBtn}
                                        color="primary"
                                        disableElevation
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            ) : ''
                    }
                </Grid>
            </Card>

        </Paper>
    );
};

/**
 * PropTypes definition for the TakeSimDetails component.
 *
 * This defines the expected data types and shapes of the props that
 * can be passed to the TakeSimDetails component to ensure proper usage.
 */
TakeSimDetails.propTypes = {
    takeSim: PropTypes.object,
    fetchDemographics: PropTypes.func,
    fetchCaseCaller: PropTypes.func,
    keywords: PropTypes.array,
    currentClip: PropTypes.object,
    isRestartLevel: PropTypes.bool,
    userResponse: PropTypes.object,
};

/**
 * PropTypes for the Navbar component.
 *
 * Defines the expected types for the props passed to the Navbar component.
 */
Navbar.propTypes = {
    keywords: PropTypes.array,
    fetchCaseCaller: PropTypes.func,
    fetchDemographics: PropTypes.func,
    currentClip: PropTypes.object,
    isRestartLevel: PropTypes.bool,
    layoutData: PropTypes.array,
    layoutIndex: PropTypes.number,
    cancel: PropTypes.bool,
    setCancel: PropTypes.func,
};

/**
 * React PropTypes definition for the History component.
 *
 * This defines the expected data types and shapes for the props
 * that the History component should receive.
 *
 * The PropTypes are used to validate the props passed to the component
 * during development, ensuring the correct type and structure.
 */
History.propTypes = {
    history: PropTypes.array,
};

/**
 * PropTypes definition for the Demographics component.
 * Defines the type and structure of props that the Demographics component expects.
 * This helps in ensuring that the component receives valid props.
 */
Demographics.propTypes = {
    keywords: PropTypes.array,
    fetchCaseCaller: PropTypes.func,
    fetchDemographics: PropTypes.func,
    currentClip: PropTypes.object,
    isRestartLevel: PropTypes.bool,
    type: PropTypes.string,
    value: PropTypes.number,
    cancel: PropTypes.bool,
    setCancel: PropTypes.func,
};

/**
 * PropTypes for the TabPanel component.
 *
 * The TabPanel component represents a container for a single tab's content.
 * It associates the content with a specific tab identifier and ensures
 * that the correct content is displayed based on the currently selected tab.
 *
 * Properties:
 * - children: Defines the content to be displayed inside the panel. This can
 *   include any valid React nodes.
 * - value: Specifies the current tab identifier. This is used to determine
 *   whether the panel should be displayed.
 * - index: Represents the tab identifier associated with this panel. It will
 *   be compared with the value property to decide the visibility of the panel.
 * - other properties are passed through to the root element for customization.
 */
TabPanel.propTypes = {
    children: PropTypes.object,
    index: PropTypes.number,
    value: PropTypes.number,
};

/**
 * Maps the state from the Redux store to the props of a component.
 * This function selects specific pieces of state and transforms them
 * into props that the connected component can use.
 *
 * @param {Object} state - The global state object from the Redux store.
 * @returns {Object} An object containing the derived props.
 * @property {Array} keywords - A list of keywords associated with the simulation.
 * @property {Object} takeSim - Data related to the caller's case details from the simulation.
 * @property {Array} history - The case history data.
 * @property {Array} levelClips - A list of simulation script clips at the current level.
 * @property {Object} currentClip - The currently active simulation clip.
 * @property {boolean} isRestartLevel - Indicates whether the simulation level is to be restarted.
 * @property {Object} userResponse - The user's response recorded in the simulation.
 */
const mapStateToProps = (state) => ({
    keywords: state.sim.createKeywords,
    takeSim: state.sf.callerCaseDetails,
    history: state.sf.caseHistory,
    levelClips: state.sim.levelClips.sim_scripts || [],
    currentClip: state.sim.currentClip,
    isRestartLevel: state.sim.isRestartLevel,
    userResponse: state.sim.userResponse,
});

/**
 * Object that maps dispatchable action creators to props for a React-Redux component.
 *
 * This object enables binding of specific action creators to the component,
 * allowing them to be called directly as props.
 *
 * Properties:
 * - fetchDemographics: Action creator for retrieving demographic-related data.
 * - fetchCaseCaller: Action creator for fetching information related to a case caller.
 * - saveCaseVaules: Action creator for saving values associated with a case.
 */
const mapDispatchToProps = {
    fetchDemographics,
    fetchCaseCaller,
    saveCaseVaules,
};

export default connect(mapStateToProps, mapDispatchToProps)(TakeSimDetails);
